// Sección PLANTILLAS SMS — CRUD de plantillas + editor con contador de
// segmentos en vivo, chips de variables y vista previa renderizada.
//
// Contexto honesto: hoy el checkout NO pide el teléfono del cliente, así que
// estas plantillas todavía no tienen destinatarios. Esta pantalla NO muestra
// ningún dato de envíos ni sugiere que se esté enviando nada: sólo redacta y
// valida el mensaje. El aviso de arriba lo deja claro.
//
// El contador se calcula en LOCAL para que responda a cada tecla, replicando
// exactamente `analyzeMessage()` de backend/src/routes/sms.js (mismo alfabeto
// GSM-7, misma tabla de extendidos, mismos 160/153 y 70/67). La vista previa
// sí va al backend (`/sms/templates/preview`), que además devuelve las stats
// del mensaje YA renderizado — que es lo que de verdad se factura.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import * as XLSX from 'xlsx';
import { G, fmt, curSym, fmtDay, fmtDayTime } from '../theme';
import { smsApi } from '../api';

// ═══════════════════════════════════════════════════════════════════════════
// Motor de segmentación — espejo EXACTO de analyzeMessage() en el backend
// (backend/src/routes/sms.js). Si el backend cambia el alfabeto, hay que
// cambiarlo aquí también o el contador mentirá.
// ═══════════════════════════════════════════════════════════════════════════

// Alfabeto básico GSM 03.38 (el ESC 0x1B se excluye a propósito).
const GSM7_BASIC = new Set([
  ...'@£$¥èéùìòÇ\nØø\rÅå',
  ...'Δ_ΦΓΛΩΠΨΣΘΞÆæßÉ',
  ...' !"#¤%&\'()*+,-./',
  ...'0123456789:;<=>?',
  ...'¡ABCDEFGHIJKLMNO',
  ...'PQRSTUVWXYZÄÖÑÜ§',
  ...'¿abcdefghijklmno',
  ...'pqrstuvwxyzäöñüà',
]);

// Tabla de extensión GSM 03.38 — cada uno cuesta DOS unidades (ESC + char).
const GSM7_EXT = new Set([...'\f^{}\\[~]|€']);

const GSM_SINGLE = 160, GSM_MULTI = 153;
const UCS2_SINGLE = 70, UCS2_MULTI = 67;

// Tope del backend (MAX_BODY_CHARS en routes/sms.js). Si se supera, el PUT/POST
// devuelve 400, así que lo bloqueamos antes de llegar ahí.
const MAX_BODY_CHARS = 1600;

function analyzeMessage(text, pricePerSegment = 0, currency = 'EUR') {
  const body = typeof text === 'string' ? text : String(text == null ? '' : text);
  let gsm = true;
  let units = 0;
  for (const ch of body) {
    if (GSM7_BASIC.has(ch)) { units += 1; continue; }
    if (GSM7_EXT.has(ch)) { units += 2; continue; }
    gsm = false;
    break;
  }
  let encoding;
  if (gsm) {
    encoding = 'GSM7';
  } else {
    encoding = 'UCS2';
    units = body.length; // unidades UTF-16, igual que el backend
  }
  const single = gsm ? GSM_SINGLE : UCS2_SINGLE;
  const multi = gsm ? GSM_MULTI : UCS2_MULTI;
  let segments;
  if (units === 0) segments = 0;
  else if (units <= single) segments = 1;
  else segments = Math.ceil(units / multi);

  const price = Number(pricePerSegment) || 0;
  const cost = Math.round(segments * price * 100000) / 100000;

  return {
    chars: [...body].length,
    units,
    encoding,
    segments,
    maxSingle: single,
    perSegment: multi,
    remaining: segments <= 1 ? Math.max(0, single - units) : Math.max(0, segments * multi - units),
    pricePerSegment: price,
    cost,
    currency: currency || 'EUR',
  };
}

// Caracteres del mensaje que están fuera de GSM-7 y por tanto lo tiran a UCS-2.
// Se usa sólo para el aviso: saber CUÁL es el carácter culpable es la mitad del
// trabajo (casi siempre una comilla curva o un emoji pegado desde Word).
function unicodeOffenders(text, max = 14) {
  const out = [];
  const seen = new Set();
  for (const ch of String(text || '')) {
    if (GSM7_BASIC.has(ch) || GSM7_EXT.has(ch)) continue;
    if (seen.has(ch)) continue;
    seen.add(ch);
    out.push(ch);
    if (out.length >= max) break;
  }
  return out;
}

// Cuántos caracteres extendidos GSM-7 hay (los que cuentan DOBLE).
function countExtended(text) {
  let n = 0;
  for (const ch of String(text || '')) if (GSM7_EXT.has(ch)) n++;
  return n;
}

// Sustituciones tipográficas que disparan UCS-2 sin que nadie se entere:
// comillas curvas, guiones largos, puntos suspensivos, espacio duro.
const TYPO_FIXES = [
  [/[‘’‛]/g, "'"],
  [/[“”‟]/g, '"'],
  [/[–—―]/g, '-'],
  [/…/g, '...'],
  [/ /g, ' '],
  [/•/g, '*'],
];

function hasTypoTraps(text) {
  return TYPO_FIXES.some(([re]) => { re.lastIndex = 0; return re.test(String(text || '')); });
}

function normalizeTypography(text) {
  let s = String(text || '');
  for (const [re, rep] of TYPO_FIXES) s = s.replace(re, rep);
  return s;
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers de presentación
// ═══════════════════════════════════════════════════════════════════════════

// fmt() aplica la divisa de visualización del panel y asume importes en EUR.
// El coste del SMS lo fija el proveedor (Twilio / SMS.to) y puede venir en otra
// divisa: en ese caso se muestra tal cual, sin convertir, para no inventar.
function money(value, currency, decimals = 4) {
  const n = Number(value);
  if (!isFinite(n)) return '--';
  const cur = String(currency || 'EUR').toUpperCase();
  if (cur === 'EUR') return fmt(n, { decimals });
  return `${curSym(cur)}${n.toFixed(decimals)}`;
}

function apiError(e, fallback) {
  return (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || fallback;
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* sin permisos / http — probamos el fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

// Variables de reserva SÓLO si /sms/variables falla: así los chips no
// desaparecen si el endpoint cae. Los tokens reales los manda el backend.
const FALLBACK_VARIABLES = [
  { token: '{{customer.name}}', label: 'Nombre del cliente' },
  { token: '{{customer.phone}}', label: 'Teléfono del cliente' },
  { token: '{{order.id}}', label: 'ID del pedido' },
  { token: '{{order.total}}', label: 'Total del pedido' },
  { token: '{{shop.name}}', label: 'Nombre de la tienda' },
  { token: '{{cart.url}}', label: 'URL del carrito' },
];

const TPL_COLS = [
  { key: 'name', label: 'Nombre', sortable: true },
  { key: 'active', label: 'Estado', sortable: true },
  { key: 'encoding', label: 'Codificación' },
  { key: 'chars', label: 'Caracteres', num: true },
  { key: 'segments', label: 'Segmentos', num: true },
  { key: 'cost', label: 'Coste/SMS', num: true },
  { key: 'body', label: 'Mensaje' },
  { key: 'updatedAt', label: 'Actualizada', sortable: true },
  { key: 'createdAt', label: 'Creada', sortable: true },
];

const DEFAULT_COLS = TPL_COLS.filter(c => c.key !== 'createdAt').map(c => c.key);
const EMPTY_FORM = { name: '', body: '', active: true };

// ═══════════════════════════════════════════════════════════════════════════
// Componente
// ═══════════════════════════════════════════════════════════════════════════

export default function SmsTemplates({ showToast }) {
  const toast = useCallback((msg, type = 'success') => {
    if (typeof showToast === 'function') showToast(msg, type);
  }, [showToast]);

  // ── Listado ──────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [applied, setApplied] = useState({ search: '', active: '' });
  const [draftSearch, setDraftSearch] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState('updatedAt');
  const [order, setOrder] = useState('desc');

  const [visibleCols, setVisibleCols] = useState(() => new Set(DEFAULT_COLS));
  const [colsOpen, setColsOpen] = useState(false);
  const [dense, setDense] = useState(false);

  // ── Tarifa del proveedor (la manda el backend dentro de stats) ───────────
  const [pricing, setPricing] = useState({ pricePerSegment: 0, currency: 'EUR' });

  // ── Variables para los chips ─────────────────────────────────────────────
  const [variables, setVariables] = useState(FALLBACK_VARIABLES);

  // ── Editor ───────────────────────────────────────────────────────────────
  const [editorOpen, setEditorOpen] = useState(false);
  const [editId, setEditId] = useState(null);       // null = plantilla nueva
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const bodyRef = useRef(null);

  // ── Vista previa (servidor) ──────────────────────────────────────────────
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  // ── Borrado ──────────────────────────────────────────────────────────────
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Carga del listado ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort, order };
      if (applied.search) params.search = applied.search;
      if (applied.active) params.active = applied.active;
      const data = await smsApi.listTemplates(params);
      const list = Array.isArray(data && data.templates) ? data.templates : [];
      setRows(list);
      setTotal(Number(data && data.total) || 0);
      // La tarifa viaja dentro de stats de cada plantilla (viene de la config
      // del proveedor del tenant). La primera fila con stats ya nos vale.
      const withStats = list.find(t => t && t.stats);
      if (withStats) {
        setPricing({
          pricePerSegment: Number(withStats.stats.pricePerSegment) || 0,
          currency: withStats.stats.currency || 'EUR',
        });
      }
    } catch (e) {
      setRows([]);
      toast(apiError(e, 'No se pudieron cargar las plantillas SMS'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, order, applied, toast]);

  useEffect(() => { load(); }, [load]);

  // Clientes alcanzables por SMS (mismo cálculo que usan las campañas). null =
  // todavía no lo sabemos: así el aviso no aparece ni desaparece a destiempo.
  const [reachable, setReachable] = useState(null);
  useEffect(() => {
    let alive = true;
    smsApi.audience()
      .then(d => { if (alive) setReachable(Number(d && d.reachable) || 0); })
      .catch(() => { /* sin dato no afirmamos nada: el aviso queda oculto */ });
    return () => { alive = false; };
  }, []);

  // Variables + tarifa al montar. La tarifa se pide con un preview vacío
  // porque es la única vía que la devuelve aunque no haya ninguna plantilla.
  useEffect(() => {
    let alive = true;
    smsApi.variables()
      .then(d => {
        if (!alive) return;
        const list = Array.isArray(d && d.variables) ? d.variables.filter(v => v && v.token) : [];
        if (list.length) setVariables(list);
      })
      .catch(() => { /* se quedan los de reserva, no molestamos con un toast */ });

    smsApi.previewTemplate({ body: '' })
      .then(d => {
        if (!alive || !d || !d.stats) return;
        setPricing({
          pricePerSegment: Number(d.stats.pricePerSegment) || 0,
          currency: d.stats.currency || 'EUR',
        });
        const list = Array.isArray(d.variables) ? d.variables.filter(v => v && v.token) : [];
        if (list.length) setVariables(prev => (prev === FALLBACK_VARIABLES ? list : prev));
      })
      .catch(() => { /* sin tarifa: los costes se muestran como "--" */ });

    return () => { alive = false; };
  }, []);

  // ── Contador local, instantáneo ──────────────────────────────────────────
  const stats = useMemo(
    () => analyzeMessage(form.body, pricing.pricePerSegment, pricing.currency),
    [form.body, pricing]
  );
  const offenders = useMemo(() => (stats.encoding === 'UCS2' ? unicodeOffenders(form.body) : []), [form.body, stats.encoding]);
  const extCount = useMemo(() => (stats.encoding === 'GSM7' ? countExtended(form.body) : 0), [form.body, stats.encoding]);
  const typoTraps = useMemo(() => hasTypoTraps(form.body), [form.body]);

  // ── Vista previa con debounce ────────────────────────────────────────────
  useEffect(() => {
    if (!editorOpen) return undefined;
    const body = form.body;
    if (!body || !body.trim()) {
      setPreview(null);
      setPreviewError('');
      setPreviewLoading(false);
      return undefined;
    }
    let alive = true;
    setPreviewLoading(true);
    const t = setTimeout(() => {
      smsApi.previewTemplate({ body })
        .then(d => {
          if (!alive) return;
          setPreview(d || null);
          setPreviewError('');
          if (d && d.stats) {
            setPricing(p => (
              Number(d.stats.pricePerSegment) === p.pricePerSegment && (d.stats.currency || 'EUR') === p.currency
                ? p
                : { pricePerSegment: Number(d.stats.pricePerSegment) || 0, currency: d.stats.currency || 'EUR' }
            ));
          }
        })
        .catch(e => {
          if (!alive) return;
          setPreview(null);
          setPreviewError(apiError(e, 'No se pudo generar la vista previa'));
        })
        .finally(() => { if (alive) setPreviewLoading(false); });
    }, 450);
    return () => { alive = false; clearTimeout(t); };
  }, [form.body, editorOpen]);

  // ── Acciones de listado ──────────────────────────────────────────────────
  const applySearch = () => { setPage(1); setApplied(a => ({ ...a, search: draftSearch })); };
  const resetFilters = () => { setDraftSearch(''); setApplied({ search: '', active: '' }); setPage(1); };
  const setActiveFilter = (v) => { setPage(1); setApplied(a => ({ ...a, active: v })); };

  const toggleSort = (key) => {
    const col = TPL_COLS.find(c => c.key === key);
    if (!col || !col.sortable) return;
    if (sort === key) setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setOrder('desc'); }
    setPage(1);
  };

  const toggleCol = (key) => setVisibleCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // ── Editor ───────────────────────────────────────────────────────────────
  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setPreview(null);
    setPreviewError('');
    setEditLoading(false);
    setEditorOpen(true);
  };

  const openEdit = async (row) => {
    if (!row || !row.id) return;
    setEditId(row.id);
    // Pintamos ya lo que tenemos en la tabla y refrescamos contra el servidor:
    // así el modal no parpadea y aun así se edita sobre la versión vigente.
    setForm({ name: row.name || '', body: row.body || '', active: row.active !== false });
    setPreview(null);
    setPreviewError('');
    setEditorOpen(true);
    setEditLoading(true);
    try {
      const fresh = await smsApi.getTemplate(row.id);
      if (fresh && fresh.id) {
        setForm({ name: fresh.name || '', body: fresh.body || '', active: fresh.active !== false });
      }
    } catch (e) {
      toast(apiError(e, 'No se pudo cargar la plantilla'), 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditor = () => {
    if (saving) return;
    setEditorOpen(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setPreview(null);
    setPreviewError('');
  };

  // Inserta la variable EN LA POSICIÓN DEL CURSOR y devuelve el foco ahí
  // detrás. Añadirla al final obligaría a recolocarla a mano cada vez.
  const insertVariable = (token) => {
    if (!token) return;
    const el = bodyRef.current;
    const current = form.body || '';
    if (!el) {
      setForm(f => ({ ...f, body: (f.body || '') + token }));
      return;
    }
    const start = typeof el.selectionStart === 'number' ? el.selectionStart : current.length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
    const next = current.slice(0, start) + token + current.slice(end);
    if (next.length > MAX_BODY_CHARS) {
      toast(`El mensaje no puede superar ${MAX_BODY_CHARS} caracteres`, 'error');
      return;
    }
    const caret = start + token.length;
    // flushSync aplica el nuevo valor al textarea YA, así el cursor se coloca
    // justo detrás de la variable sin depender de requestAnimationFrame (que no
    // se dispara en pestañas en segundo plano y dejaba el foco en el chip).
    flushSync(() => setForm(f => ({ ...f, body: next })));
    try {
      el.focus();
      el.setSelectionRange(caret, caret);
    } catch (err) { /* navegador sin soporte: da igual, el texto ya está */ }
  };

  const applyTypoFix = () => {
    setForm(f => ({ ...f, body: normalizeTypography(f.body) }));
    toast('Comillas y guiones normalizados a GSM-7');
  };

  const save = async () => {
    const name = String(form.name || '').trim();
    if (!name) { toast('Ponle un nombre a la plantilla', 'error'); return; }
    if ((form.body || '').length > MAX_BODY_CHARS) {
      toast(`El mensaje supera ${MAX_BODY_CHARS} caracteres`, 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { name, body: form.body || '', active: !!form.active };
      if (editId) {
        await smsApi.updateTemplate(editId, payload);
        toast('Plantilla guardada');
      } else {
        await smsApi.createTemplate(payload);
        toast('Plantilla creada');
      }
      setEditorOpen(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      setPreview(null);
      await load();
    } catch (e) {
      toast(apiError(e, 'No se pudo guardar la plantilla'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete || !toDelete.id) return;
    setDeleting(true);
    try {
      await smsApi.deleteTemplate(toDelete.id);
      toast('Plantilla borrada');
      setToDelete(null);
      // Si borramos la última fila de la página, retrocedemos una.
      if (rows.length === 1 && page > 1) setPage(p => p - 1);
      else await load();
    } catch (e) {
      // 409 = la usa una campaña programada o en curso; el backend lo explica.
      toast(apiError(e, 'No se pudo borrar la plantilla'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const copyId = async (id) => {
    const ok = await copyToClipboard(String(id || ''));
    toast(ok ? 'ID copiado al portapapeles' : 'No se pudo copiar el ID', ok ? 'success' : 'error');
  };

  // ── Exportación ──────────────────────────────────────────────────────────
  // Declarada ANTES de exportAll: la usa dentro (regla TDZ del proyecto).
  const exportCell = (t, key) => {
    const s = (t && t.stats) || {};
    switch (key) {
      case 'active': return t.active !== false ? 'Activa' : 'Inactiva';
      case 'encoding': return s.encoding === 'UCS2' ? 'Unicode (UCS-2)' : 'GSM-7';
      case 'chars': return Number(s.chars) || 0;
      case 'segments': return Number(s.segments) || 0;
      case 'cost': return Number(s.pricePerSegment) ? Number(s.cost || 0).toFixed(4) : '';
      case 'body': return t.body || '';
      case 'updatedAt': return fmtDay(t.updatedAt);
      case 'createdAt': return fmtDay(t.createdAt);
      default: return t[key] == null ? '' : t[key];
    }
  };

  const exportAll = async (kind) => {
    try {
      const params = { limit: 100, sort, order };
      if (applied.search) params.search = applied.search;
      if (applied.active) params.active = applied.active;
      const all = [];
      for (let p = 1; p <= 50; p++) {
        const data = await smsApi.listTemplates({ ...params, page: p });
        const batch = Array.isArray(data && data.templates) ? data.templates : [];
        all.push(...batch);
        if (batch.length < 100 || all.length >= (Number(data && data.total) || 0)) break;
      }
      const cols = TPL_COLS.filter(c => visibleCols.has(c.key));
      const header = cols.map(c => c.label);
      const body = all.map(t => cols.map(col => exportCell(t, col.key)));
      const stamp = new Date().toISOString().split('T')[0];

      if (kind === 'csv') {
        const csv = [header, ...body]
          .map(r => r.map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(','))
          .join('\n');
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `plantillas_sms_${stamp}.csv`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Plantillas SMS');
        XLSX.writeFile(wb, `plantillas_sms_${stamp}.xlsx`);
      }
      toast(`${all.length} plantilla${all.length === 1 ? '' : 's'} exportada${all.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast(apiError(e, 'Error al exportar'), 'error');
    }
  };

  // ── KPIs (sobre lo cargado; se etiqueta el alcance para no engañar) ──────
  const pageScope = rows.length < total ? 'en esta página' : 'en total';
  const activeCount = rows.filter(t => t && t.active !== false).length;
  const unicodeCount = rows.filter(t => t && t.stats && t.stats.encoding === 'UCS2').length;
  const priced = Number(pricing.pricePerSegment) > 0;
  const avgCost = rows.length
    ? rows.reduce((a, t) => a + (Number(t && t.stats && t.stats.cost) || 0), 0) / rows.length
    : 0;

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const cols = TPL_COLS.filter(c => visibleCols.has(c.key));
  const pad = dense ? '7px 12px' : undefined;

  // ── Celdas de la tabla ───────────────────────────────────────────────────
  const renderCell = (t, key) => {
    const s = (t && t.stats) || {};
    switch (key) {
      case 'name':
        return <span style={{ color: G.white, fontWeight: 600 }}>{t.name || 'Sin nombre'}</span>;
      case 'active':
        return t.active !== false
          ? <span className="badge badge-green">Activa</span>
          : <span className="badge badge-gray">Inactiva</span>;
      case 'encoding':
        return s.encoding === 'UCS2'
          ? <span className="badge badge-orange" title="Unicode: segmentos de 70 caracteres">Unicode</span>
          : <span className="badge badge-gray" title="GSM-7: segmentos de 160 caracteres">GSM-7</span>;
      case 'chars':
        return Number(s.chars) || 0;
      case 'segments': {
        const n = Number(s.segments) || 0;
        return <span style={{ color: n > 1 ? G.orange : G.dim }}>{n}</span>;
      }
      case 'cost':
        return priced ? money(s.cost, s.currency || pricing.currency) : '--';
      case 'body':
        return (
          <span
            title={t.body || ''}
            style={{ color: G.muted, display: 'inline-block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
          >
            {(t.body || '').replace(/\s+/g, ' ').trim() || '--'}
          </span>
        );
      case 'updatedAt': {
        const d = fmtDayTime(t.updatedAt);
        return <span>{d.day}{d.time ? <span style={{ color: G.muted, marginLeft: 6 }}>{d.time}</span> : null}</span>;
      }
      case 'createdAt':
        return fmtDay(t.createdAt);
      default:
        return t[key] == null || t[key] === '' ? '--' : t[key];
    }
  };

  // Barra de ocupación del segmento actual (cuánto queda para que salte a otro).
  const segPct = (() => {
    if (!stats.units) return 0;
    const cap = stats.segments <= 1 ? stats.maxSingle : stats.segments * stats.perSegment;
    return Math.min(100, Math.round((stats.units / cap) * 100));
  })();
  const segColor = stats.encoding === 'UCS2' ? G.orange : (stats.segments > 1 ? G.blue : G.gold);

  const previewStats = preview && preview.renderedStats ? preview.renderedStats : null;
  const segmentsDiffer = !!previewStats && Number(previewStats.segments) !== Number(stats.segments);

  return (
    <div>
      {/* Aviso honesto, SÓLO si de verdad no hay nadie con teléfono. */}
      {reachable === 0 && (
      <div
        style={{
          background: G.orangeDim, border: `1px solid ${G.orange}35`, borderRadius: 12,
          padding: '14px 18px', marginBottom: 22, display: 'flex', gap: 12, alignItems: 'flex-start',
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>⚠</span>
        <div style={{ fontSize: 12.5, color: G.dim, lineHeight: 1.65 }}>
          <strong style={{ color: G.orange }}>Todavía no hay destinatarios.</strong>{' '}
          Ningún cliente tiene teléfono guardado, así que ahora mismo no hay ningún número al
          que enviar estas plantillas. Puedes redactarlas, medir su coste y dejarlas listas: en
          cuanto el checkout capture el teléfono, las campañas podrán usarlas. Aquí no se envía nada.
        </div>
      </div>
      )}

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Plantillas</div>
          <div className="kpi-value">{total.toLocaleString('es-ES')}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {applied.search || applied.active ? 'con los filtros actuales' : 'creadas en tu cuenta'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Activas</div>
          <div className="kpi-value">{activeCount}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>de {rows.length} {pageScope}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Con Unicode</div>
          <div className="kpi-value" style={{ color: unicodeCount ? G.orange : undefined }}>{unicodeCount}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {unicodeCount ? 'segmentos de 70, no de 160' : `sin emojis ${pageScope}`}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Coste medio / SMS</div>
          <div className="kpi-value">{priced && rows.length ? money(avgCost, pricing.currency) : '--'}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {priced
              ? `${money(pricing.pricePerSegment, pricing.currency)} por segmento`
              : 'sin tarifa configurada en el proveedor'}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Plantillas SMS</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refrescar</button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setColsOpen(o => !o)}>▤ Columnas</button>
              {colsOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, minWidth: 200, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
                  {TPL_COLS.map(c => (
                    <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 2px', fontSize: 12, color: G.white, cursor: 'pointer' }}>
                      <input type="checkbox" checked={visibleCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                      {c.label}
                    </label>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, borderTop: `1px solid ${G.border}`, paddingTop: 10 }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setVisibleCols(new Set(TPL_COLS.map(c => c.key)))}>Todas</button>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setColsOpen(false)}>Cerrar</button>
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setDense(d => !d)}>{dense ? '↕ Normal' : '↕ Compacta'}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('csv')}>↓ CSV</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('xlsx')}>↓ Excel</button>
            <button className="btn btn-gold btn-sm" onClick={openNew}>+ Nueva</button>
          </div>
        </div>

        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="filters">
            <input
              className="search"
              placeholder="Buscar por nombre o contenido…"
              value={draftSearch}
              onChange={e => setDraftSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applySearch(); }}
            />
            <div className="pill-group">
              <button className={`pill ${applied.active === '' ? 'active' : ''}`} onClick={() => setActiveFilter('')}>Todas</button>
              <button className={`pill ${applied.active === '1' ? 'active' : ''}`} onClick={() => setActiveFilter('1')}>Activas</button>
              <button className={`pill ${applied.active === '0' ? 'active' : ''}`} onClick={() => setActiveFilter('0')}>Inactivas</button>
            </div>
            <button className="btn btn-gold btn-sm" onClick={applySearch}>Buscar</button>
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Resetear</button>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {cols.map(c => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    style={{ cursor: c.sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', textAlign: c.num ? 'right' : 'left', padding: pad }}
                    title={c.sortable ? 'Ordenar' : undefined}
                  >
                    {c.label}
                    {sort === c.key && <span style={{ color: G.gold, marginLeft: 5 }}>{order === 'asc' ? '▲' : '▼'}</span>}
                  </th>
                ))}
                <th style={{ textAlign: 'right', whiteSpace: 'nowrap', padding: pad }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={cols.length + 1} style={{ textAlign: 'center', padding: 36, color: G.muted }}>Cargando…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={cols.length + 1}>
                    <div className="empty">
                      <div className="empty-icon">💬</div>
                      {applied.search || applied.active ? (
                        <>
                          <div style={{ fontSize: 14, color: G.dim, marginBottom: 4 }}>Ninguna plantilla coincide con el filtro</div>
                          <div>Prueba a limpiar la búsqueda o cambiar el estado.</div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 14, color: G.dim, marginBottom: 4 }}>Todavía no hay plantillas SMS</div>
                          <div>Crea la primera y el editor te dirá cuántos segmentos ocupa y cuánto costaría cada envío.</div>
                          <div style={{ marginTop: 16 }}>
                            <button className="btn btn-gold btn-sm" onClick={openNew}>+ Crear la primera plantilla</button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {!loading && rows.map(t => (
                <tr key={t.id}>
                  {cols.map(col => (
                    <td
                      key={col.key}
                      style={{
                        padding: pad,
                        whiteSpace: 'nowrap',
                        textAlign: col.num ? 'right' : 'left',
                        fontFamily: col.num ? 'DM Mono, monospace' : undefined,
                      }}
                    >
                      {renderCell(t, col.key)}
                    </td>
                  ))}
                  <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}>Editar</button>
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: 6 }} onClick={() => setToDelete(t)}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 22px', borderTop: `1px solid ${G.border}`, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
            {total === 0 ? 'Sin registros' : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} de ${total.toLocaleString('es-ES')}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              className="form-input"
              style={{ width: 'auto', padding: '5px 28px 5px 10px', fontSize: 11 }}
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: 11, color: G.muted }}>por página</span>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
            <span style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', minWidth: 62, textAlign: 'center' }}>{page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
          </div>
        </div>
      </div>

      {/* ═══════════════ EDITOR ═══════════════ */}
      {editorOpen && (
        <div className="modal-bg" onMouseDown={e => { if (e.target === e.currentTarget) closeEditor(); }}>
          <div className="modal" style={{ width: 980 }}>
            <div className="modal-title">{editId ? 'Editar plantilla SMS' : 'Nueva plantilla SMS'}</div>

            {editLoading && (
              <div style={{ fontSize: 11, color: G.muted, marginTop: -14, marginBottom: 14, fontFamily: 'DM Mono, monospace' }}>
                Sincronizando con el servidor…
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
              {/* ── Columna izquierda: formulario ── */}
              <div style={{ flex: '1 1 420px', minWidth: 0 }}>
                {editId && (
                  <div className="form-group">
                    <label className="form-label">ID de plantilla</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input className="form-input mono" readOnly value={editId} style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: G.dim }} />
                      <button className="btn btn-ghost btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => copyId(editId)}>⧉ Copiar</button>
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nombre</label>
                    <input
                      className="form-input"
                      placeholder="Carrito abandonado — recordatorio 1h"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      maxLength={120}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-input"
                      value={form.active ? '1' : '0'}
                      onChange={e => setForm(f => ({ ...f, active: e.target.value === '1' }))}
                    >
                      <option value="1">Activa</option>
                      <option value="0">Inactiva</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Variables disponibles</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {variables.map(v => (
                      <button
                        key={v.token}
                        type="button"
                        className="tag"
                        title={`Insertar ${v.token}${v.label ? ` — ${v.label}` : ''}${v.example ? ` (ej.: ${v.example})` : ''}`}
                        // Sin esto el chip roba el foco al textarea y se pierde el cursor.
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => insertVariable(v.token)}
                        style={{ cursor: 'pointer', border: `1px solid ${G.border}`, padding: '4px 9px', color: G.goldLight, background: G.goldDim }}
                      >
                        {v.token}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 10.5, color: G.muted, marginTop: 7, lineHeight: 1.6 }}>
                    Se insertan donde tengas el cursor. Una variable sin dato se sustituye por texto vacío.
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label">Mensaje</label>
                  <textarea
                    ref={bodyRef}
                    className="form-input"
                    rows={7}
                    maxLength={MAX_BODY_CHARS}
                    placeholder="Hola {{customer.name}}, tu pedido {{order.id}} sigue esperándote: {{cart.url}}"
                    value={form.body}
                    onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                    style={{ fontFamily: 'DM Mono, monospace', fontSize: 12.5, lineHeight: 1.7, resize: 'vertical', minHeight: 130 }}
                  />
                </div>

                {/* ── Contador en vivo ── */}
                <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                    <Counter label="Caracteres" value={stats.chars} />
                    <Counter label="Unidades" value={stats.units} hint={`máx. ${stats.maxSingle} en 1 segmento`} />
                    <Counter
                      label="Segmentos"
                      value={stats.segments}
                      color={stats.segments > 1 ? G.orange : G.white}
                    />
                    <Counter
                      label="Codificación"
                      value={stats.encoding === 'UCS2' ? 'UCS-2' : 'GSM-7'}
                      color={stats.encoding === 'UCS2' ? G.orange : G.white}
                    />
                    <Counter label="Quedan" value={stats.remaining} hint="en el último segmento" />
                    <Counter
                      label="Coste estimado"
                      value={priced ? money(stats.cost, stats.currency) : '--'}
                      hint={priced ? `${money(stats.pricePerSegment, stats.currency)} × segmento` : 'sin tarifa configurada'}
                      color={priced ? G.goldLight : G.muted}
                    />
                  </div>
                  <div className="progress" style={{ marginTop: 12 }}>
                    <div className="progress-fill" style={{ width: `${segPct}%`, background: segColor }} />
                  </div>
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 6, fontFamily: 'DM Mono, monospace' }}>
                    {stats.segments <= 1
                      ? `${stats.units} / ${stats.maxSingle} unidades en 1 segmento`
                      : `${stats.units} / ${stats.segments * stats.perSegment} unidades en ${stats.segments} segmentos (${stats.perSegment} por segmento al concatenar)`}
                  </div>
                </div>

                {/* ── Aviso Unicode: la trampa que dispara la factura ── */}
                {stats.encoding === 'UCS2' && (
                  <div style={{ marginTop: 12, background: G.orangeDim, border: `1px solid ${G.orange}45`, borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: 12.5, color: G.orange, fontWeight: 700, marginBottom: 6 }}>
                      ⚠ Unicode detectado — cada segmento pasa de 160 a 70 caracteres
                    </div>
                    <div style={{ fontSize: 11.5, color: G.dim, lineHeight: 1.7 }}>
                      Un solo carácter fuera del alfabeto GSM-7 (emoji, comilla curva, guion largo…)
                      convierte <strong>todo</strong> el mensaje a UCS-2. Con {stats.units} unidades
                      salen <strong>{stats.segments} segmento{stats.segments === 1 ? '' : 's'}</strong>;
                      en GSM-7 ese mismo texto cabría en {Math.max(1, Math.ceil(stats.units / GSM_MULTI))}.
                      El coste por SMS se multiplica igual.
                    </div>
                    {offenders.length > 0 && (
                      <div style={{ fontSize: 11, color: G.dim, marginTop: 8 }}>
                        Caracteres implicados:{' '}
                        {offenders.map((ch, i) => (
                          <span key={i} className="tag" style={{ marginRight: 4, fontSize: 12 }}>{ch === '\n' ? '↵' : ch}</span>
                        ))}
                      </div>
                    )}
                    {typoTraps && (
                      <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={applyTypoFix}>
                        Normalizar comillas, guiones y puntos suspensivos
                      </button>
                    )}
                  </div>
                )}

                {stats.encoding === 'GSM7' && extCount > 0 && (
                  <div style={{ marginTop: 12, fontSize: 11, color: G.dim, background: G.blueDim, border: `1px solid ${G.blue}30`, borderRadius: 10, padding: '10px 14px', lineHeight: 1.6 }}>
                    <strong style={{ color: G.blue }}>{extCount} carácter{extCount === 1 ? '' : 'es'} extendido{extCount === 1 ? '' : 's'}</strong>{' '}
                    {'(^ { } [ ] ~ | \\ €)'}: cada uno ocupa <strong>dos</strong> unidades, no una. Por eso
                    hay {stats.units} unidades con {stats.chars} caracteres.
                  </div>
                )}

                {(form.body || '').length >= MAX_BODY_CHARS && (
                  <div style={{ marginTop: 12, fontSize: 11, color: G.red, background: G.redDim, border: `1px solid ${G.red}30`, borderRadius: 10, padding: '10px 14px' }}>
                    Has llegado al límite de {MAX_BODY_CHARS} caracteres que acepta el servidor.
                  </div>
                )}
              </div>

              {/* ── Columna derecha: vista previa ── */}
              <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                <label className="form-label">Vista previa con datos de ejemplo</label>
                <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 12, padding: 16, minHeight: 150 }}>
                  {!form.body.trim() && (
                    <div style={{ fontSize: 12, color: G.muted, lineHeight: 1.7 }}>
                      Escribe el mensaje y aquí verás cómo le llega al cliente, con las variables ya sustituidas.
                    </div>
                  )}
                  {form.body.trim() && previewLoading && !preview && (
                    <div style={{ fontSize: 12, color: G.muted }}>Generando vista previa…</div>
                  )}
                  {form.body.trim() && previewError && (
                    <div style={{ fontSize: 12, color: G.red, lineHeight: 1.6 }}>{previewError}</div>
                  )}
                  {form.body.trim() && preview && (
                    <div
                      style={{
                        background: G.surface, borderRadius: '14px 14px 14px 4px',
                        padding: '12px 14px', fontSize: 12.5, color: G.white,
                        lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        opacity: previewLoading ? 0.55 : 1, transition: 'opacity 0.2s',
                      }}
                    >
                      {preview.rendered || <span style={{ color: G.muted }}>(mensaje vacío tras sustituir las variables)</span>}
                    </div>
                  )}
                </div>

                {previewStats && (
                  <div style={{ marginTop: 14, background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'DM Mono, monospace', marginBottom: 10 }}>
                      Ya con las variables sustituidas
                    </div>
                    <StatLine label="Caracteres" value={previewStats.chars} />
                    <StatLine label="Unidades" value={previewStats.units} />
                    <StatLine
                      label="Segmentos"
                      value={previewStats.segments}
                      color={Number(previewStats.segments) > 1 ? G.orange : undefined}
                    />
                    <StatLine
                      label="Codificación"
                      value={previewStats.encoding === 'UCS2' ? 'UCS-2' : 'GSM-7'}
                      color={previewStats.encoding === 'UCS2' ? G.orange : undefined}
                    />
                    <StatLine
                      label="Coste"
                      value={Number(previewStats.pricePerSegment) ? money(previewStats.cost, previewStats.currency) : '--'}
                      color={Number(previewStats.pricePerSegment) ? G.goldLight : G.muted}
                    />
                    {segmentsDiffer && (
                      <div style={{ fontSize: 11, color: G.dim, marginTop: 10, lineHeight: 1.6, borderTop: `1px solid ${G.border}`, paddingTop: 10 }}>
                        Ojo: el texto con variables ocupa <strong>{stats.segments}</strong> segmento{stats.segments === 1 ? '' : 's'},
                        pero una vez sustituidas son <strong style={{ color: G.orange }}>{previewStats.segments}</strong>.
                        Lo que se factura es esto último, y con nombres largos o acentuados puede subir aún más.
                      </div>
                    )}
                    <div style={{ fontSize: 10.5, color: G.muted, marginTop: 10, lineHeight: 1.6 }}>
                      Datos de ejemplo del servidor; cada cliente real dará un tamaño distinto.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={closeEditor} disabled={saving}>Cancelar</button>
              <button className="btn btn-gold" onClick={save} disabled={saving || !String(form.name || '').trim()}>
                {saving ? 'Guardando…' : (editId ? 'Guardar cambios' : 'Crear plantilla')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ CONFIRMAR BORRADO ═══════════════ */}
      {toDelete && (
        <div className="modal-bg" onMouseDown={e => { if (e.target === e.currentTarget && !deleting) setToDelete(null); }}>
          <div className="modal" style={{ width: 460 }}>
            <div className="modal-title">Borrar plantilla</div>
            <div style={{ fontSize: 13, color: G.dim, lineHeight: 1.7 }}>
              Vas a borrar <strong style={{ color: G.white }}>{toDelete.name || 'esta plantilla'}</strong>.
              Es permanente y no se puede deshacer. Si alguna campaña programada la usa, el servidor
              se negará a borrarla y te lo dirá.
            </div>
            <div style={{ marginTop: 14, background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: '10px 14px', fontFamily: 'DM Mono, monospace', fontSize: 11.5, color: G.muted, maxHeight: 110, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {toDelete.body || '(sin mensaje)'}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setToDelete(null)} disabled={deleting}>Cancelar</button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Borrando…' : 'Borrar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Piezas pequeñas ────────────────────────────────────────────────────────

function Counter({ label, value, hint, color }) {
  return (
    <div style={{ minWidth: 76 }}>
      <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: 'DM Mono, monospace' }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontFamily: 'DM Mono, monospace', color: color || G.white, marginTop: 4 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 9.5, color: G.muted, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function StatLine({ label, value, color }) {
  return (
    <div className="stat-line" style={{ padding: '7px 0' }}>
      <span style={{ fontSize: 11.5, color: G.muted }}>{label}</span>
      <span style={{ fontSize: 12.5, fontFamily: 'DM Mono, monospace', color: color || G.white }}>{value}</span>
    </div>
  );
}
